export interface RuntimeConfig {
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  auth0Scope: string;
  identityConfigured: boolean;
  apiBase: string;
  gameUrl: string;
  environment: string;
}

export class ConfigError extends Error {
  readonly missing: string[];

  constructor(message: string, missing: string[] = []) {
    super(message);
    this.name = "ConfigError";
    this.missing = missing;
  }
}

type Environment = Record<string, string | undefined>;

const REQUIRED_KEYS = [
  "VITE_AUTH0_DOMAIN",
  "VITE_AUTH0_CLIENT_ID",
  "VITE_AUTH0_AUDIENCE",
  "VITE_VAELOR_API_BASE",
  "VITE_VAELOR_GAME_URL",
] as const;

const REQUIRED_SCOPES = [
  "openid",
  "profile",
  "email",
  "game:play",
  "realtime:connect",
  "chat:read",
  "chat:write",
  "chat:report",
] as const;

const PLACEHOLDER_AUTH0_DOMAINS = new Set([
  "tenant.eu.auth0.com",
  "replace-with-your-auth0-tenant.eu.auth0.com",
]);

const PLACEHOLDER_AUTH0_CLIENT_IDS = new Set([
  "public-spa-client",
  "visual-check-spa",
  "replace-with-spa-client-id",
]);

export function isIdentityConfigured(domain: string, clientId: string): boolean {
  const normalizedDomain = domain.trim().toLowerCase();
  const normalizedClientId = clientId.trim().toLowerCase();
  return !(
    PLACEHOLDER_AUTH0_DOMAINS.has(normalizedDomain) ||
    PLACEHOLDER_AUTH0_CLIENT_IDS.has(normalizedClientId) ||
    normalizedDomain.startsWith("replace-with-") ||
    normalizedClientId.startsWith("replace-with-")
  );
}

function parseHttpUrl(value: string, key: string, allowQuery: boolean): URL {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new ConfigError(`${key} doit être une URL valide.`);
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new ConfigError(`${key} doit utiliser HTTP ou HTTPS.`);
  }
  if (url.username || url.password) {
    throw new ConfigError(`${key} ne doit contenir aucun identifiant.`);
  }
  if (!allowQuery && (url.search || url.hash)) {
    throw new ConfigError(`${key} ne doit contenir ni paramètres ni ancre.`);
  }
  return url;
}

function parseApiUrl(value: string, runtimeOrigin?: string): URL {
  if (value === "/api") {
    if (!runtimeOrigin) {
      throw new ConfigError(
        "VITE_VAELOR_API_BASE=/api nécessite l’origine courante du portail.",
      );
    }
    const originUrl = parseHttpUrl(runtimeOrigin, "L’origine du portail", false);
    if (originUrl.pathname !== "/") {
      throw new ConfigError("L’origine du portail ne doit contenir aucun chemin.");
    }
    return new URL("/api", originUrl.origin);
  }
  if (value.startsWith("/")) {
    throw new ConfigError(
      "VITE_VAELOR_API_BASE doit être une URL HTTP(S) absolue ou le chemin même origine exact « /api ».",
    );
  }
  return parseHttpUrl(value, "VITE_VAELOR_API_BASE", false);
}

export function normalizeScopes(value?: string): string {
  const scopes = new Set(
    (value ?? "")
      .split(/\s+/)
      .map((scope) => scope.trim())
      .filter(Boolean),
  );
  for (const scope of REQUIRED_SCOPES) scopes.add(scope);
  return [...scopes].join(" ");
}

export function parseConfig(environment: Environment, runtimeOrigin?: string): RuntimeConfig {
  const missing = REQUIRED_KEYS.filter((key) => !environment[key]?.trim());
  if (missing.length > 0) {
    throw new ConfigError("La configuration du portail joueur est incomplète.", [...missing]);
  }

  const auth0Domain = environment.VITE_AUTH0_DOMAIN!.trim().toLowerCase();
  const auth0ClientId = environment.VITE_AUTH0_CLIENT_ID!.trim();
  if (
    auth0Domain.includes("://") ||
    auth0Domain.includes("/") ||
    auth0Domain.includes(" ") ||
    !/^[a-z0-9.-]+$/.test(auth0Domain)
  ) {
    throw new ConfigError(
      "VITE_AUTH0_DOMAIN doit être un nom d’hôte Auth0, sans protocole ni chemin.",
    );
  }

  const apiUrl = parseApiUrl(
    environment.VITE_VAELOR_API_BASE!.trim(),
    runtimeOrigin,
  );
  const gameUrl = parseHttpUrl(
    environment.VITE_VAELOR_GAME_URL!.trim(),
    "VITE_VAELOR_GAME_URL",
    true,
  );
  const environmentName = environment.VITE_VAELOR_ENVIRONMENT?.trim().toLowerCase() || "local";
  const identityConfigured = isIdentityConfigured(auth0Domain, auth0ClientId);
  if ((environmentName === "staging" || environmentName === "production") && !identityConfigured) {
    throw new ConfigError(
      "Le déploiement refuse les identifiants Auth0 de démonstration.",
      ["VITE_AUTH0_DOMAIN", "VITE_AUTH0_CLIENT_ID"],
    );
  }

  return {
    auth0Domain,
    auth0ClientId,
    auth0Audience: environment.VITE_AUTH0_AUDIENCE!.trim(),
    auth0Scope: normalizeScopes(environment.VITE_AUTH0_SCOPE),
    identityConfigured,
    apiBase: apiUrl.toString().replace(/\/$/, ""),
    gameUrl: gameUrl.toString(),
    environment: environmentName,
  };
}

export function loadConfig(): RuntimeConfig {
  return parseConfig(import.meta.env, window.location.origin);
}

export function callbackUrl(location: Pick<Location, "origin">, basePath: string): string {
  return new URL(basePath, `${location.origin}/`).toString();
}
